import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  Search,
  X,
  Users,
  Clock,
  Video,
  Loader2,
  MessageSquare,
  User,
  Lock,
} from 'lucide-react';
import { getLeads } from '@/api';
import {
  getLeadsOptions,
  getLeadSessionsOptions,
  getLeadSessionRoomsOptions,
} from '@/api/generated/@tanstack/react-query.gen';

export const Route = createFileRoute('/_auth/leads')({
  component: LeadsPageWrapper,
});

const leadsHeaders = () => ({
  'x-leads-password': sessionStorage.getItem('leads-pw') || '',
});

function LeadsPageWrapper() {
  const [unlocked, setUnlocked] = useState(
    () => !!sessionStorage.getItem('leads-pw'),
  );
  const [pw, setPw] = useState('');
  const [error, setError] = useState(false);

  const handleUnlock = async () => {
    try {
      const { data, error } = await getLeads({
        query: { page: 1, page_size: 1 },
        headers: { 'x-leads-password': pw },
      });
      if (data) {
        sessionStorage.setItem('leads-pw', pw);
        setUnlocked(true);
      } else if (error) {
        setError(true);
      }
    } catch {
      setError(true);
    }
  };

  if (!unlocked) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-80 space-y-4 text-center">
          <Lock className="h-8 w-8 mx-auto text-gray-400" />
          <h2 className="text-lg font-semibold">Leads Access</h2>
          <Input
            type="password"
            placeholder="Enter password"
            value={pw}
            onChange={(e) => { setPw(e.target.value); setError(false); }}
            onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
          />
          {error && <p className="text-sm text-red-500">Wrong password</p>}
          <Button className="w-full" onClick={handleUnlock}>Unlock</Button>
        </div>
      </div>
    );
  }

  return <LeadsPage />;
}

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}

function ExpandChevron({
  show,
  open,
  size,
}: {
  show: boolean;
  open: boolean;
  size: 'h-3 w-3' | 'h-4 w-4';
}) {
  if (!show) return <div className={size} />;
  const Icon = open ? ChevronDown : ChevronRight;
  return <Icon className={`${size} text-gray-400`} />;
}

function ExpandedRooms({ sessionTemplateId }: { sessionTemplateId: string }) {
  const { data, isLoading } = useQuery(
    getLeadSessionRoomsOptions({
      path: { session_template_id: sessionTemplateId },
      headers: leadsHeaders(),
    }),
  );

  const [openTranscript, setOpenTranscript] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="pl-6 py-2 flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading rooms...
      </div>
    );
  }

  if (!data?.rooms?.length) {
    return (
      <div className="pl-6 py-2 text-xs text-muted-foreground">No rooms yet.</div>
    );
  }

  return (
    <div className="pl-6 py-2 space-y-1.5">
      {data.rooms.map((room) => {
        const dialogue = room.dialogue ?? [];
        const isOpen = openTranscript === room.id;
        return (
          <div key={room.id}>
            <div
              className="flex items-center gap-3 px-3 py-1.5 bg-gray-50 rounded border border-gray-100 text-xs cursor-pointer hover:bg-gray-100"
              onClick={(e) => {
                e.stopPropagation();
                setOpenTranscript(isOpen ? null : room.id);
              }}>
              <div className="flex items-center gap-1 shrink-0">
                <ExpandChevron show={dialogue.length > 0} open={isOpen} size="h-3 w-3" />
              </div>
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <User className="h-3 w-3 text-gray-400 shrink-0" />
                <span className="font-medium truncate">
                  {[room.first_name, room.last_name].filter(Boolean).join(' ') || 'Anonymous'}
                </span>
                {room.email && (
                  <span className="text-muted-foreground truncate">({room.email})</span>
                )}
              </div>
              <Badge
                variant="outline"
                className={`text-[10px] shrink-0 ${
                  room.status === 'completed'
                    ? 'border-emerald-300 text-emerald-700'
                    : 'border-gray-200 text-gray-500'
                }`}>
                {room.status || 'unknown'}
              </Badge>
              <span className="text-muted-foreground shrink-0">
                <Clock className="h-3 w-3 inline mr-0.5" />
                {formatDuration(room.duration_seconds)}
              </span>
              {dialogue.length > 0 && (
                <span className="text-muted-foreground shrink-0">
                  <MessageSquare className="h-3 w-3 inline mr-0.5" />
                  {dialogue.length}
                </span>
              )}
              <span className="text-muted-foreground shrink-0">
                {formatDate(room.created_at)}
              </span>
            </div>

            {isOpen && dialogue.length > 0 && (
              <div className="ml-6 mt-1 mb-2 border-l-2 border-gray-200 pl-3 space-y-1.5 max-h-80 overflow-y-auto">
                {dialogue.map((msg, i) => (
                  <div key={i} className="text-xs">
                    <span className={`font-semibold ${
                      msg.role === 'assistant' ? 'text-blue-600' : 'text-gray-700'
                    }`}>
                      {msg.role === 'assistant' ? 'AI' : 'Candidate'}:
                    </span>{' '}
                    <span className="text-gray-600">{msg.content}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ExpandedSessions({ userId }: { userId: string }) {
  const { data, isLoading } = useQuery(
    getLeadSessionsOptions({
      path: { user_id: userId },
      headers: leadsHeaders(),
    }),
  );

  const [expandedSession, setExpandedSession] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="px-8 py-4 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading sessions...
      </div>
    );
  }

  if (!data?.sessions?.length) {
    return (
      <div className="px-8 py-4 text-sm text-muted-foreground">
        No sessions created yet.
      </div>
    );
  }

  return (
    <div className="px-8 py-3 space-y-2">
      <div className="flex items-center gap-4 mb-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Sessions ({data.total_sessions})
        </p>
        <span className="text-xs text-gray-400">
          {data.total_rooms} rooms · {data.total_completed_rooms} completed
        </span>
      </div>
      {data.sessions.map((session) => {
        const hasRooms = (session.total_rooms ?? 0) > 0;
        const isOpen = expandedSession === session.id;
        return (
          <div key={session.id}>
            <div
              className="flex items-center gap-4 px-3 py-2 bg-white rounded-md border border-gray-100 text-sm cursor-pointer hover:bg-gray-50"
              onClick={(e) => {
                e.stopPropagation();
                setExpandedSession(isOpen ? null : session.id);
              }}>
              <div className="shrink-0">
                <ExpandChevron show={hasRooms} open={isOpen} size="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-medium truncate block">
                  {session.name || 'Untitled Session'}
                </span>
                <span className="text-xs text-muted-foreground">
                  {session.session_type || 'general'} · {formatDate(session.created_at)}
                </span>
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-500 shrink-0">
                <Video className="h-3 w-3" />
                {session.total_rooms} rooms
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-500 shrink-0">
                <Users className="h-3 w-3" />
                {session.completed_rooms} completed
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-500 shrink-0">
                <Clock className="h-3 w-3" />
                {formatDuration(session.avg_duration_seconds)}
              </div>
              <Badge
                variant="outline"
                className={`text-xs shrink-0 ${
                  session.visibility === 'open'
                    ? 'border-green-300 text-green-700'
                    : 'border-gray-300 text-gray-600'
                }`}>
                {session.visibility}
              </Badge>
            </div>

            {isOpen && hasRooms && <ExpandedRooms sessionTemplateId={session.id} />}
          </div>
        );
      })}
    </div>
  );
}

function LeadsPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState('registered_at');
  const [order, setOrder] = useState('desc');
  const [workEmailsOnly, setWorkEmailsOnly] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 400);
  };

  const { data, isLoading } = useQuery(
    getLeadsOptions({
      query: {
        page,
        page_size: pageSize,
        sort,
        order,
        search: debouncedSearch || undefined,
        work_emails_only: workEmailsOnly || undefined,
      },
      headers: leadsHeaders(),
    }),
  );

  const toggleRow = (userId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (!next.delete(userId)) next.add(userId);
      return next;
    });
  };

  const totalPages = data?.total_pages || 0;

  const pageButtons = [
    { icon: ChevronsLeft, target: 1, disabled: page <= 1 },
    { icon: ChevronLeft, target: page - 1, disabled: page <= 1 },
    { icon: ChevronRight, target: page + 1, disabled: page >= totalPages },
    { icon: ChevronsRight, target: totalPages, disabled: page >= totalPages },
  ];

  return (
    <div className="p-4 max-w-7xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-semibold mb-1">Leads</h1>
        <p className="text-sm text-muted-foreground">
          Analyze registered users and their activity
        </p>
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9 h-9 bg-gray-50/50 border-gray-200"
          />
          {search && (
            <button
              onClick={() => {
                setSearch('');
                setDebouncedSearch('');
                setPage(1);
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <Select value={sort} onValueChange={(v) => { setSort(v); setPage(1); }}>
          <SelectTrigger className="w-44 h-9">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="registered_at">Registered</SelectItem>
            <SelectItem value="total_rooms">Total Rooms</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="first_name">Name</SelectItem>
          </SelectContent>
        </Select>

        <Select value={order} onValueChange={(v) => { setOrder(v); setPage(1); }}>
          <SelectTrigger className="w-32 h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="desc">Newest first</SelectItem>
            <SelectItem value="asc">Oldest first</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant={workEmailsOnly ? 'default' : 'outline'}
          size="sm"
          className="h-9"
          onClick={() => { setWorkEmailsOnly(!workEmailsOnly); setPage(1); }}>
          Work emails only
        </Button>

        {data && (
          <span className="text-sm text-muted-foreground ml-auto">
            {data.total_count} users
          </span>
        )}
      </div>

      <div className="rounded-sm border border-gray-200 overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50/50">
              <TableHead className="w-8" />
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="min-w-[120px]">Company</TableHead>
              <TableHead>Registered</TableHead>
              <TableHead className="text-center">Rooms</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 8 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell />
                  {Array.from({ length: 6 }, (_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full rounded" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data?.leads?.length ? (
              data.leads.map((lead) => {
                const isExpanded = expandedRows.has(lead.user_id);
                const hasRooms = (lead.total_rooms ?? 0) > 0;
                return (
                  <Fragment key={lead.user_id}>
                    <TableRow
                      className="cursor-pointer hover:bg-gray-50/50"
                      onClick={() => toggleRow(lead.user_id)}>
                      <TableCell className="w-8 px-2">
                        {hasRooms && (
                          <ExpandChevron show open={isExpanded} size="h-4 w-4" />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {lead.first_name} {lead.last_name}
                        {lead.position_title && (
                          <span className="block text-xs text-muted-foreground">
                            {lead.position_title}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{lead.email}</TableCell>
                      <TableCell className="min-w-[120px]">
                        <span className="text-sm font-medium">
                          {lead.company_name || '-'}
                        </span>
                        {lead.organization_size && (
                          <span className="block text-xs text-muted-foreground">
                            {lead.organization_size}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {timeAgo(lead.registered_at)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary" className="text-xs">
                          {lead.total_rooms}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {lead.email_verified && (
                            <Badge
                              variant="outline"
                              className="text-xs border-blue-300 text-blue-700 bg-blue-50">
                              Verified
                            </Badge>
                          )}
                          {lead.onboarding_completed && (
                            <Badge
                              variant="outline"
                              className="text-xs border-emerald-300 text-emerald-700 bg-emerald-50">
                              Onboarded
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>

                    {isExpanded && hasRooms && (
                      <TableRow className="bg-gray-50/30">
                        <TableCell colSpan={7} className="p-0">
                          <ExpandedSessions userId={lead.user_id} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="h-32 text-center text-gray-500 text-sm">
                  No leads found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 0 && (
        <div className="flex items-center justify-between py-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">Rows per page</span>
            <Select
              value={`${pageSize}`}
              onValueChange={(v) => {
                setPageSize(Number(v));
                setPage(1);
              }}>
              <SelectTrigger className="h-8 w-16 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent side="top">
                {[10, 20, 50, 100].map((size) => (
                  <SelectItem key={size} value={`${size}`} className="text-xs">
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-xs text-gray-600 font-medium">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              {pageButtons.map(({ icon: Icon, target, disabled }, i) => (
                <Button
                  key={i}
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setPage(target)}
                  disabled={disabled}>
                  <Icon className="h-3 w-3" />
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
